import { useRouter } from 'next/router'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'

import { DroppointInfoTemplateV2 } from '@Domains/Checkout/Steps/Delivery/Components/DroppointItem/DroppointInfoTemplate'
import { addDropPointInfoToGMap } from '@Domains/Checkout/Steps/Delivery/utils/Droppoint.utils'
import { addMarksToMap, createMapScript, getMapBoundsV2 } from '@Domains/Checkout/Steps/Delivery/utils/Map.utils'
import {
  DeliveryDropPointAddress,
  useFormatOpeningHours,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutShippingStep/Components/DeliveryDropPointDialog/DeliveryDropPointDialog'
import { MetadataContext } from '@Globals/Metadata'
import { CheckoutV2ServiceLocation } from '@Pages/onestepcheckout'

import styles from './DeliveryDropPointMap.module.scss'

interface DeliveryDropPointMapProps {
  address?: DeliveryDropPointAddress | null
  onMarkerClick: (serviceLocation: CheckoutV2ServiceLocation) => void
  selectedDropPointId?: string | null
  serviceLocations: CheckoutV2ServiceLocation[] | null
}

let infoBox: google.maps.InfoWindow | null = null

export const DeliveryDropPointMap: FC<DeliveryDropPointMapProps> = ({
  address,
  onMarkerClick,
  selectedDropPointId,
  serviceLocations,
}) => {
  const { locale } = useRouter()
  const { store } = useContext(MetadataContext)
  const { formatOpeningHours } = useFormatOpeningHours()
  const countryCode = store?.config?.countryCode || ''

  const mapElement = useRef<HTMLDivElement>(null)
  const infoBoxTemplate = useRef<HTMLDivElement>(null)

  const [mapsInserted, setMapsInserted] = useState(false)

  const handleMarkerSelection = (id: string) => {
    if (!serviceLocations) {
      return
    }

    const droppoint = serviceLocations.find(item => item.externalID === id)
    if (droppoint) {
      onMarkerClick(droppoint)
    }
  }

  const handleMarkerClick = (place: CheckoutV2ServiceLocation, gmap, marker) => {
    // Close all infoWindows
    google.maps.event.trigger(gmap, 'click')

    // Create new infoWindow
    if (!infoBoxTemplate.current) {
      return
    }

    const infoBoxElement = addDropPointInfoToGMap(
      {
        city: place.city || '',
        distance: place.distance || 0,
        gmap,
        handleSelection: handleMarkerSelection,
        id: place.externalID!,
        locale: locale || '',
        name: place.name || '',
        openingClassName: `${styles.openingHour}`,
        openingHours: (place.openingHours || [])?.map(formatOpeningHours),
        postalCode: place.postalCode || '',
        streetName: place.streetName || '',
        templateRef: infoBoxTemplate.current,
      },
    )

    if (infoBox) {
      infoBox.close()
    }

    infoBox = new google.maps.InfoWindow({
      content: infoBoxElement,
      disableAutoPan: true,
    })

    infoBox.open(gmap, marker)

    const latlng = marker.getPosition()
    if (latlng) {
      gmap.setCenter(latlng)
      gmap.panBy(0, -100)
    }
  }

  const setMapOnUpdate = () => {
    if (!serviceLocations?.length || !mapElement.current) {
      return
    }

    const mapStyles = [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ]
    // Show map
    const centerCoords = getMapBoundsV2(serviceLocations)
    const gmap = new google.maps.Map(mapElement.current, {
      zoom: 13,
      center: centerCoords?.center,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      zoomControl: true,
      disableDoubleClickZoom: true,
      keyboardShortcuts: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: mapStyles,
    })

    // Center map based on service points
    // - We run it again after 50 ms to make sure it is centered
    gmap.fitBounds(
      new google.maps.LatLngBounds(
        centerCoords?.bounds.min,
        centerCoords?.bounds.max,
      ),
    )
    setTimeout(() => {
      gmap.fitBounds(
        new google.maps.LatLngBounds(
          centerCoords?.bounds.min,
          centerCoords?.bounds.max,
        ),
      )
    }, 50)

    // Add home marker
    const geocoder = new google.maps.Geocoder()
    const searchAddress = `${address?.streetName} ${address?.postalCode}, ${address?.city} ${countryCode}`

    geocoder.geocode({ address: searchAddress }, (results, status) => {
      if (status === 'OK' && results?.length) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const homeMarker = new google.maps.Marker({
          map: gmap,
          position: results[0].geometry.location,
          icon: 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt657dd64acf056ee4/map-pin-home.png',
          clickable: false,
        })
      }
    })

    addMarksToMap(gmap, serviceLocations, 'externalID', handleMarkerClick, selectedDropPointId)
  }

  useEffect(() => {
    if (mapsInserted) {
      setMapOnUpdate()
    } else {
      createMapScript(setMapOnUpdate)
      setMapsInserted(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceLocations])

  return (
    <div className={styles.deliveryDropPointMap}>
      <div
        className={styles.map}
        ref={mapElement}
      />
      <DroppointInfoTemplateV2 ref={infoBoxTemplate} />
    </div>
  )
}
